import { Container, ContainerChild, Sprite, Texture } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetProgressBar,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { TimerService } from "../Services/TimerService";

import {
  renderProgressBarDesignSvg,
  type ProgressBarDesign,
} from "../../../../data/progressBarDesignsCatalog";

import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "./svgTextureRaster";

/**
 * Compute the integer fill percentage (0–100) for the current instant from a
 * progress-bar widget's `startDate`/`targetDate`. Mirrors `computeValue` in
 * `ProgressBarWidget` exactly (same defaults / clamping) so the design and
 * legacy renders agree frame-for-frame — but returns the percentage directly
 * (independent of `max`, which only scales the `{value}` label).
 */
const computePct = (
  o: IStudioWidgetProgressBar["options"],
  nowMs: number,
): number => {
  if (!o.targetDate) return 0;
  const target = new Date(o.targetDate).getTime();
  if (Number.isNaN(target)) return 0;
  const start = o.startDate
    ? new Date(o.startDate).getTime()
    : target - 5 * 60 * 1000;
  if (Number.isNaN(start) || target <= start) return 0;
  const ratio = (nowMs - start) / (target - start);
  return Math.round(Math.max(0, Math.min(1, ratio)) * 100);
};

/**
 * Design-store render strategy for the ProgressBar widget — the percent-driven
 * sibling of `CountDownWidget/Themes/DesignRenderer`. When the widget's
 * `options.designId` resolves to a {@link ProgressBarDesign}, this renders that
 * design's CARD SVG (from the local catalog) as a single Pixi sprite stretched
 * to the widget bounds, kept live by re-rasterising the texture only when the
 * integer fill percentage changes (≤100 rasters across the whole fill window,
 * driven by the same `TimerService` tick the legacy bar uses) — never every
 * animation frame.
 *
 * One instance per widget (the parent `ProgressBarWidgetRenderer` keeps a Map
 * keyed by widget id), so multiple progress bars with different designs coexist.
 */
export class ProgressBarDesignRenderer {
  private container: Container | null = null;

  private sprite: Sprite | null = null;

  /** Widget bounds the sprite is stretched to — re-asserted after each swap. */
  private destWidth = 1;

  private destHeight = 1;

  /** Backing-store multiplier for the rasterised card bitmap. */
  private renderScale = 1;

  /** Last integer percentage baked into the current texture. */
  private lastPct: number | null = null;

  /** Texture currently shown — destroyed before being replaced. */
  private texture: Texture | null = null;

  /** Bumped per re-raster so a slow async decode can't apply out of order. */
  private generation = 0;

  private destroyed = false;

  private timerCallback: ((time: number) => void) | null = null;

  private options: IStudioWidgetProgressBar["options"] | null = null;

  constructor(private readonly design: ProgressBarDesign) {}

  render(
    widget: IStudioWidget,
    parentContainer: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const options = (widget as IStudioWidgetProgressBar).options;
    this.options = options;

    this.container = new Container();
    parentContainer.addChild(this.container);

    this.destWidth = Math.max(1, destination.width);
    this.destHeight = Math.max(1, destination.height);

    // Backing-store resolution: rasterise at the larger of the card's native
    // size and the on-canvas size, capped so an oversized widget can't ask for
    // a multi-thousand-px bitmap. The sprite is then scaled to the bounds.
    const scaleForBounds = Math.max(
      1,
      this.destWidth / this.design.cardWidth,
      this.destHeight / this.design.cardHeight,
    );
    this.renderScale = Math.min(scaleForBounds, 3);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.width = this.destWidth;
    sprite.height = this.destHeight;
    sprite.position.set(0, 0);
    this.container.addChild(sprite as unknown as ContainerChild);
    this.sprite = sprite;

    // Draw the first frame immediately with whatever fonts are already decoded
    // so the slot is never empty; the post-font draw upgrades the typeface once
    // the design's Google fonts are ready.
    void this.rerasterize(computePct(options, Date.now()));
    void ensureDesignFontsReady(this.design.fonts).then(() => {
      if (this.destroyed) return;
      // Redraw with the CURRENT fill now that the real fonts are decoded (the
      // first draw may have used a system fallback). Force it past the guard.
      this.lastPct = null;
      void this.rerasterize(computePct(options, Date.now()));
    });

    const update = (nowMs: number) => {
      if (this.destroyed || !this.options) return;
      const pct = computePct(this.options, nowMs);
      // Re-raster ONLY when the integer percentage actually changed — not every
      // tick and never every frame.
      if (pct !== this.lastPct) {
        void this.rerasterize(pct);
      }
    };

    update(Date.now());
    this.timerCallback = update;
    TimerService.getInstance().subscribe(update);
  }

  /**
   * (Re)build the card SVG → texture for the given fill percentage and swap it
   * onto the sprite. Generation-guarded so a slow decode that resolves after a
   * newer one can't clobber the fresher frame, and after teardown.
   */
  private async rerasterize(pct: number): Promise<void> {
    if (this.destroyed || !this.sprite) return;

    // Claim this generation/pct immediately so the equality guard in the tick
    // callback doesn't fire a second raster for the same pct mid-decode.
    this.lastPct = pct;
    const gen = ++this.generation;

    const svg = renderProgressBarDesignSvg(this.design, pct);

    try {
      const texture = await rasterizeSvgToTexture(
        svg,
        this.design.cardWidth,
        this.design.cardHeight,
        this.renderScale,
      );

      // A newer raster (or teardown) won the race — drop this one.
      if (
        this.destroyed ||
        gen !== this.generation ||
        !this.sprite ||
        this.sprite.destroyed
      ) {
        texture.destroy(true);
        return;
      }

      const old = this.texture;
      this.texture = texture;
      this.sprite.texture = texture;
      // Assigning a texture in Pixi v8 resets the sprite to the texture's
      // intrinsic (backing-store) size — re-assert the widget bounds so the
      // card always fills `destination` regardless of renderScale.
      this.sprite.width = this.destWidth;
      this.sprite.height = this.destHeight;
      if (old && old !== Texture.EMPTY) old.destroy(true);
    } catch {
      // Decode failed (malformed SVG / browser quirk) — keep the previous frame
      // on screen rather than blanking. Allow a future tick to retry.
      if (!this.destroyed && gen === this.generation) {
        this.lastPct = null;
      }
    }
  }

  destroy(): void {
    this.destroyed = true;

    if (this.timerCallback) {
      TimerService.getInstance().unsubscribe(this.timerCallback);
      this.timerCallback = null;
    }

    if (this.texture && this.texture !== Texture.EMPTY) {
      try {
        this.texture.destroy(true);
      } catch {
        /* best-effort */
      }
    }
    this.texture = null;
    this.sprite = null;
    this.options = null;

    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
    }
  }
}
